import { ReactElement, useState, useRef, useEffect } from "react";
import { Box, Tooltip, SxProps, Theme, useTheme, useMediaQuery } from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";

interface InfoTooltipIconProps {
  /**
   * Tooltip message to display when hovering over the icon
   * This is required to keep the component abstract and reusable
   */
  tooltipMessage: string;
  /**
   * Custom sx styles for the icon
   * @default Uses default positioning styles for Select fields
   */
  sx?: SxProps<Theme>;
  /**
   * Position of the icon
   * @default "select" - positions to the left of dropdown arrow (for Select/FormControl)
   * "inline" - for inline use with custom positioning
   */
  position?: "select" | "inline";
  /**
   * Icon size
   * @default "small" - 16px for inline use, 18px for select position
   */
  size?: "small" | "medium" | "large";
}

/**
 * Reusable Info Tooltip Icon Component
 * 
 * Displays an info icon with a customizable tooltip message for providing
 * additional information or context to users.
 * 
 * @example
 * // For use with Select/FormControl (default positioning)
 * <FormControl fullWidth required sx={{ position: "relative" }}>
 *   <InputLabel>Field Name</InputLabel>
 *   <Select>...</Select>
 *   <InfoTooltipIcon 
 *     tooltipMessage="Additional information about this field"
 *   />
 * </FormControl>
 * 
 * @example
 * // For inline use with custom positioning
 * <InfoTooltipIcon 
 *   tooltipMessage="This field is required"
 *   position="inline"
 *   sx={{ ml: 1, verticalAlign: "middle" }}
 * />
 * 
 * @example
 * // With custom size
 * <InfoTooltipIcon 
 *   tooltipMessage="Help text"
 *   size="small"
 *   sx={{ ml: 0.5 }}
 * />
 */
function InfoTooltipIcon({
  tooltipMessage,
  sx,
  position = "select",
  size = "small",
}: InfoTooltipIconProps): ReactElement {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [open, setOpen] = useState(false);
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => {
    if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
  }, []);

  const sizeMap = {
    small: position === "select" ? 18 : 16,
    medium: position === "select" ? 20 : 18,
    large: position === "select" ? 24 : 22,
  };

  const fontSize = sizeMap[size];

  const defaultSx: SxProps<Theme> = position === "select" 
    ? {
        fontSize: fontSize,
        color: "text.secondary",
        cursor: "help",
        position: "absolute",
        right: 40, // Position to the left of dropdown arrow
        top: "50%",
        mt: 0.5, // Slight adjustment for alignment
        transform: "translateY(-50%)",
        pointerEvents: "auto",
        zIndex: 1,
      }
    : {
        fontSize: fontSize,
        color: "text.secondary",
        cursor: "help",
      };

  const icon = <InfoIcon sx={{ ...defaultSx, ...sx }} />;

  if (isMobile) {
    const handleOpen = () => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
        hideTimeoutRef.current = null;
      }
      setOpen(true);
      hideTimeoutRef.current = setTimeout(() => setOpen(false), 1000);
    };
    return (
      <Tooltip
        title={tooltipMessage}
        arrow
        placement="top"
        open={open}
        onClose={() => setOpen(false)}
        disableHoverListener
        disableFocusListener
        disableTouchListener
        TransitionProps={{ timeout: 400 }}
      >
        <Box
          component="span"
          onClick={(e) => {
            e.preventDefault();
            handleOpen();
          }}
          sx={{ display: "inline-flex", cursor: "help" }}
        >
          {icon}
        </Box>
      </Tooltip>
    );
  }

  return (
    <Tooltip title={tooltipMessage} arrow placement="top">
      {icon}
    </Tooltip>
  );
}

export default InfoTooltipIcon;
